import { useMemo, useState } from 'react'
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import {
  ClockCircleOutlined,
  LockOutlined,
  ReloadOutlined,
  SafetyOutlined,
  StopOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/ru'

import { orgsAPI } from '@/api/orgs'
import { fetchUsers, type UserItem } from '@/api/users'
import {
  platformRbacAPI,
  type PlatformPermissionItem,
  type PlatformRoleItem,
  type PlatformScopeType,
  type RoleAssignmentCreate,
  type RoleAssignmentItem,
  type TemporaryElevationCreate,
  type TemporaryElevationItem,
} from '@/api/platformRbac'
import { useCapabilities } from '@/hooks/useCapabilities'

dayjs.locale('ru')

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

interface AssignmentFormValues {
  user_id: string
  role_key: string
  scope_type: PlatformScopeType
  scope_id?: string
  reason: string
  expires_at?: Dayjs
}

interface ElevationFormValues {
  user_id: string
  scope_type: PlatformScopeType
  scope_id?: string
  permissions: string[]
  reason: string
  expires_at: Dayjs
}

const SCOPE_LABELS: Record<PlatformScopeType, string> = {
  global: 'Вся платформа',
  org: 'Организация',
  entry: 'BI-объект',
}

const ROLE_COLORS: Record<string, string> = {
  platform_owner: 'red',
  platform_developer: 'volcano',
  platform_admin: 'purple',
  security_auditor: 'blue',
  support: 'cyan',
  org_admin: 'gold',
  analyst: 'default',
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'без срока'
  return dayjs(value).format('DD.MM.YYYY HH:mm')
}

function userLabel(user: UserItem) {
  return `${user.full_name || user.email} · ${user.email}${user.org_code ? ` · ${user.org_code}` : ''}`
}

function renderUser(user: RoleAssignmentItem['user'] | TemporaryElevationItem['user']) {
  return (
    <Space direction="vertical" size={0}>
      <Text strong>{user.full_name || user.email}</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {user.email}
      </Text>
      {user.org_code ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          базовая организация: {user.org_code} · {user.org_name || 'организация'}
        </Text>
      ) : null}
    </Space>
  )
}

function renderScope(scopeType: PlatformScopeType, scopeLabel: string) {
  return (
    <Space direction="vertical" size={0}>
      <Tag color={scopeType === 'global' ? 'red' : scopeType === 'org' ? 'blue' : 'default'}>
        {SCOPE_LABELS[scopeType]}
      </Tag>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {scopeLabel}
      </Text>
    </Space>
  )
}

function getErrorText(error: unknown, fallback: string) {
  const detail = (error as any)?.response?.data?.detail
  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || item.message || String(item)).join('; ')
  }
  return detail || fallback
}

export default function PlatformRbacPage() {
  const queryClient = useQueryClient()
  const [assignmentForm] = Form.useForm<AssignmentFormValues>()
  const [elevationForm] = Form.useForm<ElevationFormValues>()
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>()
  const capabilities = useCapabilities()
  const canManageRbac = capabilities.platformRbacManage

  const assignmentScopeType = Form.useWatch('scope_type', assignmentForm) || 'global'
  const elevationScopeType = Form.useWatch('scope_type', elevationForm) || 'org'

  const rolesQuery = useQuery({
    queryKey: ['platform-rbac-roles'],
    queryFn: platformRbacAPI.listRoles,
  })
  const permissionsQuery = useQuery({
    queryKey: ['platform-rbac-permissions'],
    queryFn: platformRbacAPI.listPermissions,
  })
  const assignmentsQuery = useQuery({
    queryKey: ['platform-rbac-assignments', selectedUserId],
    queryFn: () => platformRbacAPI.listAssignments({ user_id: selectedUserId, active_only: true }),
  })
  const elevationsQuery = useQuery({
    queryKey: ['platform-rbac-elevations', selectedUserId],
    queryFn: () => platformRbacAPI.listElevations({ user_id: selectedUserId, active_only: true }),
  })
  const usersQuery = useQuery({
    queryKey: ['users', { page: 1, page_size: 1000, is_active: true }],
    queryFn: () => fetchUsers({ page: 1, page_size: 1000, is_active: true }),
  })
  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: orgsAPI.list,
  })

  const invalidateRbac = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['platform-rbac-assignments'] }),
      queryClient.invalidateQueries({ queryKey: ['platform-rbac-elevations'] }),
      queryClient.invalidateQueries({ queryKey: ['audit-log'] }),
    ])
  }

  const assignmentMutation = useMutation({
    mutationFn: (data: RoleAssignmentCreate) => platformRbacAPI.createAssignment(data),
    onSuccess: async () => {
      assignmentForm.resetFields()
      await invalidateRbac()
      message.success('Роль назначена')
    },
    onError: (error) => {
      message.error(getErrorText(error, 'Не удалось назначить роль'))
    },
  })

  const revokeAssignmentMutation = useMutation({
    mutationFn: platformRbacAPI.revokeAssignment,
    onSuccess: async () => {
      await invalidateRbac()
      message.success('Роль отозвана')
    },
    onError: (error) => {
      message.error(getErrorText(error, 'Не удалось отозвать роль'))
    },
  })

  const elevationMutation = useMutation({
    mutationFn: (data: TemporaryElevationCreate) => platformRbacAPI.createElevation(data),
    onSuccess: async () => {
      elevationForm.resetFields()
      await invalidateRbac()
      message.success('Временный доступ выдан')
    },
    onError: (error) => {
      message.error(getErrorText(error, 'Не удалось выдать временный доступ'))
    },
  })

  const revokeElevationMutation = useMutation({
    mutationFn: platformRbacAPI.revokeElevation,
    onSuccess: async () => {
      await invalidateRbac()
      message.success('Временный доступ отозван')
    },
    onError: (error) => {
      message.error(getErrorText(error, 'Не удалось отозвать временный доступ'))
    },
  })

  const users = usersQuery.data?.items || []
  const roles = rolesQuery.data || []
  const permissions = permissionsQuery.data || []
  const organizations = orgsQuery.data || []

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: userLabel(user),
      })),
    [users],
  )

  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        value: role.key,
        label: `${role.name} · ${role.key}`,
      })),
    [roles],
  )

  const permissionOptions = useMemo(
    () =>
      permissions
        .filter((permission) => permission.key !== '*')
        .map((permission) => ({
          value: permission.key,
          label: `${permission.name} · ${permission.key}`,
        })),
    [permissions],
  )

  const orgOptions = useMemo(
    () =>
      organizations.map((org) => ({
        value: org.id,
        label: `${org.code} · ${org.short_name || org.name}`,
      })),
    [organizations],
  )

  const roleByKey = useMemo(() => {
    const map = new Map<string, PlatformRoleItem>()
    for (const role of roles) {
      map.set(role.key, role)
    }
    return map
  }, [roles])

  const permissionByKey = useMemo(() => {
    const map = new Map<string, PlatformPermissionItem>()
    for (const permission of permissions) {
      map.set(permission.key, permission)
    }
    return map
  }, [permissions])

  const isMutating =
    assignmentMutation.isPending ||
    revokeAssignmentMutation.isPending ||
    elevationMutation.isPending ||
    revokeElevationMutation.isPending

  const handleAssignmentScopeChange = () => {
    assignmentForm.setFieldValue('scope_id', undefined)
  }

  const handleElevationScopeChange = () => {
    elevationForm.setFieldValue('scope_id', undefined)
  }

  const handleCreateAssignment = async () => {
    const values = await assignmentForm.validateFields()
    await assignmentMutation.mutateAsync({
      user_id: values.user_id,
      role_key: values.role_key,
      scope_type: values.scope_type,
      scope_id: values.scope_type === 'global' ? null : values.scope_id,
      reason: values.reason,
      expires_at: values.expires_at ? values.expires_at.toISOString() : null,
    })
  }

  const handleCreateElevation = async () => {
    const values = await elevationForm.validateFields()
    await elevationMutation.mutateAsync({
      user_id: values.user_id,
      scope_type: values.scope_type,
      scope_id: values.scope_type === 'global' ? null : values.scope_id,
      permissions: values.permissions,
      reason: values.reason,
      expires_at: values.expires_at.toISOString(),
    })
  }

  const handleRefresh = async () => {
    await Promise.all([
      rolesQuery.refetch(),
      permissionsQuery.refetch(),
      assignmentsQuery.refetch(),
      elevationsQuery.refetch(),
      usersQuery.refetch(),
      orgsQuery.refetch(),
    ])
  }

  const confirmRevokeAssignment = (assignment: RoleAssignmentItem) => {
    Modal.confirm({
      title: 'Отозвать платформенную роль?',
      content: `${assignment.role_name} у ${assignment.user.email}. Действие попадет в аудит.`,
      okText: 'Отозвать',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: () => revokeAssignmentMutation.mutateAsync(assignment.id),
    })
  }

  const confirmRevokeElevation = (elevation: TemporaryElevationItem) => {
    Modal.confirm({
      title: 'Отозвать временный доступ?',
      content: `${elevation.permissions.join(', ')} у ${elevation.user.email}. Действие попадет в аудит.`,
      okText: 'Отозвать',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: () => revokeElevationMutation.mutateAsync(elevation.id),
    })
  }

  const assignmentColumns: ColumnsType<RoleAssignmentItem> = [
    {
      title: 'Пользователь',
      key: 'user',
      render: (_, record) => renderUser(record.user),
    },
    {
      title: 'Роль',
      key: 'role',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={ROLE_COLORS[record.role_key] || 'default'}>{record.role_name}</Tag>
          <Text code style={{ fontSize: 12 }}>
            {record.role_key}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Область действия',
      key: 'scope',
      width: 220,
      render: (_, record) => renderScope(record.scope_type, record.scope_label),
    },
    {
      title: 'Причина / срок',
      key: 'reason',
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text>{record.reason || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            выдал: {record.assigned_by_email || '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            срок: {formatDate(record.expires_at)}
          </Text>
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button
          danger
          size="small"
          icon={<StopOutlined />}
          loading={revokeAssignmentMutation.isPending}
          disabled={isMutating || !canManageRbac}
          onClick={() => confirmRevokeAssignment(record)}
        >
          Отозвать
        </Button>
      ),
    },
  ]

  const elevationColumns: ColumnsType<TemporaryElevationItem> = [
    {
      title: 'Пользователь',
      key: 'user',
      render: (_, record) => renderUser(record.user),
    },
    {
      title: 'Права',
      key: 'permissions',
      render: (_, record) => (
        <Space wrap>
          {record.permissions.map((permission) => (
            <Tooltip key={permission} title={permissionByKey.get(permission)?.description || permission}>
              <Tag color="blue">{permissionByKey.get(permission)?.name || permission}</Tag>
            </Tooltip>
          ))}
        </Space>
      ),
    },
    {
      title: 'Область действия',
      key: 'scope',
      width: 220,
      render: (_, record) => renderScope(record.scope_type, record.scope_label),
    },
    {
      title: 'Причина / срок',
      key: 'reason',
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text>{record.reason}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            согласовал: {record.approved_by_email || '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            до: {formatDate(record.expires_at)}
          </Text>
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button
          danger
          size="small"
          icon={<StopOutlined />}
          loading={revokeElevationMutation.isPending}
          disabled={isMutating || !canManageRbac}
          onClick={() => confirmRevokeElevation(record)}
        >
          Отозвать
        </Button>
      ),
    },
  ]

  const renderScopeField = (scopeType: PlatformScopeType, requiredMessage: string) => {
    if (scopeType === 'global') return null
    if (scopeType === 'org') {
      return (
        <Form.Item
          name="scope_id"
          label="Организация"
          rules={[{ required: true, message: requiredMessage }]}
        >
          <Select
            showSearch
            placeholder="Выберите организацию"
            options={orgOptions}
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
      )
    }
    return (
      <Form.Item
        name="scope_id"
        label="BI-объект"
        rules={[{ required: true, message: requiredMessage }]}
      >
        <Input placeholder="Datalens entry/workbook id" />
      </Form.Item>
    )
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Breadcrumb
        items={[
          { title: 'Админка' },
          { title: 'Роли и права' },
        ]}
      />

      <Row justify="space-between" gutter={[16, 16]}>
        <Col>
          <Space align="center">
            <SafetyOutlined style={{ fontSize: 28 }} />
            <div>
              <Title level={2} style={{ margin: 0 }}>
                Роли и права платформы
              </Title>
              <Text type="secondary">
                Управление разработчиками, администраторами, поддержкой и временными повышениями.
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space>
            <Select
              allowClear
              showSearch
              placeholder="Фильтр по пользователю"
              style={{ width: 360 }}
              options={userOptions}
              value={selectedUserId}
              onChange={setSelectedUserId}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              Обновить
            </Button>
          </Space>
        </Col>
      </Row>

      <Alert
        showIcon
        type={canManageRbac ? 'info' : 'warning'}
        message="Это платформенный контур прав, не BI ACL/RLS."
        description={
          canManageRbac
            ? 'BI-видимость дашбордов и доступ к данным остаются в BI/RLS-разделах. Здесь назначаются права на администрирование платформы и краткоживущие диагностические доступы с обязательной причиной и аудитом.'
            : 'У вас режим просмотра: можно видеть роли, назначения и временные повышения, но создавать и отзывать их нельзя.'
        }
      />

      {canManageRbac ? (
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <Card
              title={<Space><LockOutlined /> Назначить платформенную роль</Space>}
              extra={<Tag color="warning">постоянное право</Tag>}
            >
              <Form
                form={assignmentForm}
                layout="vertical"
                initialValues={{ scope_type: 'global' }}
                disabled={isMutating}
              >
                <Form.Item
                  name="user_id"
                  label="Пользователь"
                  rules={[{ required: true, message: 'Выберите пользователя' }]}
                >
                  <Select
                    showSearch
                    placeholder="Кому назначить роль"
                    options={userOptions}
                    loading={usersQuery.isLoading}
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
                <Form.Item
                  name="role_key"
                  label="Роль"
                  rules={[{ required: true, message: 'Выберите роль' }]}
                >
                  <Select
                    placeholder="Платформенная роль"
                    options={roleOptions}
                    loading={rolesQuery.isLoading}
                  />
                </Form.Item>
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item name="scope_type" label="Область действия" rules={[{ required: true }]}>
                      <Select
                        onChange={handleAssignmentScopeChange}
                        options={[
                          { value: 'global', label: 'Вся платформа' },
                          { value: 'org', label: 'Организация' },
                          { value: 'entry', label: 'BI-объект' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    {renderScopeField(assignmentScopeType, 'Укажите область действия роли')}
                  </Col>
                </Row>
                <Form.Item name="expires_at" label="Срок действия">
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    placeholder="Без срока, если пусто"
                    disabledDate={(date) => date && date < dayjs().startOf('day')}
                  />
                </Form.Item>
                <Form.Item
                  name="reason"
                  label="Причина"
                  rules={[
                    { required: true, message: 'Нужна причина для аудита' },
                    { min: 5, message: 'Минимум 5 символов' },
                  ]}
                >
                  <TextArea rows={3} placeholder="Например: подключение нового администратора ЛПУ" />
                </Form.Item>
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  loading={assignmentMutation.isPending}
                  onClick={handleCreateAssignment}
                >
                  Назначить роль
                </Button>
              </Form>
            </Card>
          </Col>

          <Col xs={24} xl={12}>
            <Card
              title={<Space><ClockCircleOutlined /> Временное повышение</Space>}
              extra={<Tag color="processing">до 24 часов</Tag>}
            >
              <Form
                form={elevationForm}
                layout="vertical"
                initialValues={{ scope_type: 'org', expires_at: dayjs().add(1, 'hour') }}
                disabled={isMutating}
              >
                <Form.Item
                  name="user_id"
                  label="Пользователь"
                  rules={[{ required: true, message: 'Выберите пользователя' }]}
                >
                  <Select
                    showSearch
                    placeholder="Кому выдать временный доступ"
                    options={userOptions}
                    loading={usersQuery.isLoading}
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
                <Form.Item
                  name="permissions"
                  label="Права"
                  rules={[{ required: true, message: 'Выберите права' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Только конкретные права, wildcard запрещен"
                    options={permissionOptions}
                    loading={permissionsQuery.isLoading}
                  />
                </Form.Item>
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item name="scope_type" label="Область действия" rules={[{ required: true }]}>
                      <Select
                        onChange={handleElevationScopeChange}
                        options={[
                          { value: 'org', label: 'Организация' },
                          { value: 'entry', label: 'BI-объект' },
                          { value: 'global', label: 'Вся платформа' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    {renderScopeField(elevationScopeType, 'Укажите область действия временного доступа')}
                  </Col>
                </Row>
                <Form.Item
                  name="expires_at"
                  label="Истекает"
                  rules={[{ required: true, message: 'Укажите срок до 24 часов' }]}
                >
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    disabledDate={(date) => date && date < dayjs().startOf('day')}
                  />
                </Form.Item>
                <Form.Item
                  name="reason"
                  label="Причина"
                  rules={[
                    { required: true, message: 'Нужна причина для аудита' },
                    { min: 10, message: 'Минимум 10 символов' },
                  ]}
                >
                  <TextArea rows={3} placeholder="Например: диагностика инцидента доступа #..." />
                </Form.Item>
                <Button
                  type="primary"
                  icon={<ClockCircleOutlined />}
                  loading={elevationMutation.isPending}
                  onClick={handleCreateElevation}
                >
                  Выдать временный доступ
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>
      ) : null}

      <Card
        title="Активные роли"
        extra={<Tag>{assignmentsQuery.data?.length ?? 0}</Tag>}
      >
        <Table
          rowKey="id"
          columns={assignmentColumns}
          dataSource={assignmentsQuery.data || []}
          loading={assignmentsQuery.isLoading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Card
        title="Активные временные повышения"
        extra={<Tag>{elevationsQuery.data?.length ?? 0}</Tag>}
      >
        <Table
          rowKey="id"
          columns={elevationColumns}
          dataSource={elevationsQuery.data || []}
          loading={elevationsQuery.isLoading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Card title="Матрица ролей">
        <Row gutter={[16, 16]}>
          {roles.map((role) => (
            <Col xs={24} md={12} xl={8} key={role.key}>
              <Card
                size="small"
                title={<Tag color={ROLE_COLORS[role.key] || 'default'}>{role.name}</Tag>}
                extra={role.is_system ? <Tag>system</Tag> : null}
              >
                <Paragraph type="secondary" style={{ minHeight: 44 }}>
                  {role.description || 'Описание роли не задано'}
                </Paragraph>
                <Divider style={{ margin: '12px 0' }} />
                <Space wrap>
                  {(roleByKey.get(role.key)?.permissions || []).map((permissionKey) => (
                    <Tooltip
                      key={permissionKey}
                      title={permissionByKey.get(permissionKey)?.description || permissionKey}
                    >
                      <Tag color="blue">
                        {permissionByKey.get(permissionKey)?.name || permissionKey}
                      </Tag>
                    </Tooltip>
                  ))}
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      <Card title="Что это упрощает">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Text strong>Разработчики Медцифры</Text>
            <Paragraph type="secondary">
              Даем глобальные роли владельца/разработчика только тем, кто реально администрирует платформу.
            </Paragraph>
          </Col>
          <Col xs={24} md={8}>
            <Text strong>Администраторы ЛПУ</Text>
            <Paragraph type="secondary">
              Ограничиваем права областью конкретной организации, не выдавая доступ ко всей платформе.
            </Paragraph>
          </Col>
          <Col xs={24} md={8}>
            <Text strong>Поддержка и инциденты</Text>
            <Paragraph type="secondary">
              Краткоживущие повышения заменяют ручные исключения и остаются в журнале аудита.
            </Paragraph>
          </Col>
        </Row>
        <Text type="secondary">
          Ваш режим: {canManageRbac ? 'управление ролями и временными доступами' : 'просмотр без изменений'}.
        </Text>
      </Card>
    </Space>
  )
}
